import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { api, resolveAssetUrl } from "@/lib/api";

const SITE_URL = "https://dogaparquet.com";

function absoluteUrl(path) {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${SITE_URL}${path}`;
}

export default function ProjectDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [allProjects, setAllProjects] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setNotFound(false);
        setProject(null);
        (async () => {
            try {
                const [oneRes, listRes] = await Promise.all([
                    api.get(`/portfolio/${slug}`),
                    api.get("/portfolio"),
                ]);
                if (cancelled) return;
                setProject(oneRes.data);
                setAllProjects(listRes.data);
            } catch (err) {
                if (cancelled) return;
                if (err?.response?.status === 404) {
                    setNotFound(true);
                } else {
                    setNotFound(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (notFound) {
        return (
            <main
                data-testid="project-not-found"
                className="min-h-screen flex flex-col items-center justify-center text-cream bg-ink-900 px-6 pt-32"
            >
                <p className="text-xs uppercase tracking-widest text-brass">
                    404
                </p>
                <h1 className="mt-4 font-serif text-4xl lg:text-5xl">
                    Progetto non trovato
                </h1>
                <button
                    type="button"
                    onClick={() => navigate("/#portfolio")}
                    className="mt-8 link-underline text-sm uppercase tracking-widest text-brass"
                    data-testid="project-not-found-back"
                >
                    Torna al portfolio
                </button>
            </main>
        );
    }

    if (!project) {
        return (
            <main
                data-testid="project-loading"
                className="min-h-screen flex items-center justify-center bg-ink-900 text-cream/40 text-sm tracking-widest uppercase"
            >
                Caricamento progetto…
            </main>
        );
    }

    const index = allProjects.findIndex((p) => p.slug === slug);
    const next =
        allProjects.length > 0
            ? allProjects[(index + 1) % allProjects.length]
            : null;
    const detailEntries = project.details ? Object.entries(project.details) : [];
    const gallery = project.gallery || [];

    const projectJsonLd = {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name: project.title,
        description: project.description || project.excerpt,
        image: gallery.length
            ? gallery.map((g) => absoluteUrl(g))
            : project.cover
              ? [absoluteUrl(project.cover)]
              : undefined,
        creator: { "@id": `${SITE_URL}/#business` },
        locationCreated: project.location
            ? { "@type": "Place", name: project.location }
            : undefined,
        dateCreated: project.year,
        url: `${SITE_URL}/portfolio/${project.slug}`,
        keywords: project.category,
    };

    return (
        <main
            data-testid={`project-detail-${project.slug}`}
            className="bg-ink-900 text-cream"
        >
            <SEO
                title={`${project.title} · ${project.category || "Portfolio"}`}
                description={
                    project.excerpt ||
                    project.description?.slice(0, 200) ||
                    "Progetto realizzato da Dogaparquet di Gianluca Occhipinti."
                }
                path={`/portfolio/${project.slug}`}
                image={project.cover ? absoluteUrl(project.cover) : undefined}
                type="article"
                keywords={`${project.category}, ${project.location}, parquet Modica, Dogaparquet`}
                jsonLd={projectJsonLd}
            />
            {/* Hero */}
            <section className="relative min-h-[88vh] flex items-end overflow-hidden pt-24">
                <div className="absolute inset-0">
                    <img
                        src={resolveAssetUrl(project.cover)}
                        alt={project.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/50 to-ink-900/40" />
                </div>
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pb-20 w-full">
                    <Link
                        to="/#portfolio"
                        data-testid="project-back-link"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cream/70 hover:text-brass transition-colors"
                    >
                        <ArrowLeft size={14} /> Tutti i progetti
                    </Link>
                    <p className="mt-8 text-xs uppercase tracking-widest text-brass">
                        {[project.category, project.location, project.year]
                            .filter(Boolean)
                            .join(" · ")}
                    </p>
                    <h1
                        data-testid="project-title"
                        className="mt-4 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl"
                    >
                        {project.title}
                    </h1>
                    {project.excerpt && (
                        <p
                            data-testid="project-excerpt"
                            className="mt-6 text-lg lg:text-xl text-cream/75 max-w-2xl leading-relaxed"
                        >
                            {project.excerpt}
                        </p>
                    )}
                </div>
            </section>

            {/* Description + details */}
            {(project.description || detailEntries.length > 0) && (
                <section className="py-24 lg:py-32">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        <Reveal className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-widest text-brass">
                                Il progetto
                            </p>
                            <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-light leading-snug">
                                Un intervento curato in ogni dettaglio.
                            </h2>
                            <p
                                data-testid="project-description"
                                className="mt-8 text-base lg:text-lg text-cream/70 leading-relaxed whitespace-pre-wrap"
                            >
                                {project.description}
                            </p>
                        </Reveal>

                        {detailEntries.length > 0 && (
                            <Reveal className="lg:col-span-5" delay={120}>
                                <div className="border border-white/10 p-8 lg:p-10 bg-ink-800">
                                    <p className="text-xs uppercase tracking-widest text-brass mb-8">
                                        Scheda tecnica
                                    </p>
                                    <dl
                                        data-testid="project-details"
                                        className="space-y-5"
                                    >
                                        {detailEntries.map(([k, v]) => (
                                            <div
                                                key={k}
                                                className="flex items-baseline justify-between gap-6 pb-4 border-b border-white/5 last:border-b-0 last:pb-0"
                                            >
                                                <dt className="text-xs uppercase tracking-widest text-cream/50">
                                                    {k}
                                                </dt>
                                                <dd className="font-serif text-lg text-cream text-right">
                                                    {v}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </section>
            )}

            {/* Gallery */}
            {gallery.length > 0 && (
                <section className="pb-24 lg:pb-32">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                        <Reveal>
                            <p className="text-xs uppercase tracking-widest text-brass mb-8">
                                Galleria
                            </p>
                        </Reveal>
                        <div
                            data-testid="project-gallery"
                            className="grid grid-cols-1 md:grid-cols-6 gap-4 lg:gap-6"
                        >
                            {gallery.map((src, i) => (
                                <Reveal
                                    key={`${src}-${i}`}
                                    delay={Math.min(i * 80, 320)}
                                    className={
                                        i % 3 === 0
                                            ? "md:col-span-4 aspect-[16/10]"
                                            : "md:col-span-2 aspect-[4/5]"
                                    }
                                >
                                    <div className="relative w-full h-full overflow-hidden bg-ink-700">
                                        <img
                                            src={resolveAssetUrl(src)}
                                            alt={`${project.title} — foto ${i + 1}`}
                                            className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                                        />
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA + next */}
            <section className="border-t border-white/5 py-20">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <Link
                        to="/#contatti"
                        data-testid="project-cta-preventivo"
                        className="group inline-flex items-center justify-between border border-white/10 hover:border-brass p-8 lg:p-10 transition-colors duration-700"
                    >
                        <div>
                            <p className="text-xs uppercase tracking-widest text-brass mb-3">
                                Hai un progetto simile?
                            </p>
                            <h3 className="font-serif text-2xl lg:text-3xl font-light">
                                Richiedi un preventivo
                            </h3>
                        </div>
                        <span className="shrink-0 w-12 h-12 rounded-full border border-cream/30 group-hover:bg-brass group-hover:border-brass flex items-center justify-center transition-all duration-700">
                            <ArrowRight
                                size={18}
                                className="group-hover:text-ink-900"
                            />
                        </span>
                    </Link>
                    {next && next.slug !== slug && (
                        <Link
                            to={`/portfolio/${next.slug}`}
                            data-testid="project-next-link"
                            className="group inline-flex items-center justify-between p-8 lg:p-10 bg-ink-800 hover:bg-ink-700 transition-colors duration-700"
                        >
                            <div>
                                <p className="text-xs uppercase tracking-widest text-brass mb-3">
                                    Progetto successivo
                                </p>
                                <h3 className="font-serif text-2xl lg:text-3xl font-light">
                                    {next.title}
                                </h3>
                            </div>
                            <ArrowRight
                                size={20}
                                className="text-cream group-hover:text-brass group-hover:translate-x-1 transition-all duration-700"
                            />
                        </Link>
                    )}
                </div>
            </section>
        </main>
    );
}
